<template>
    <div
        class="c-accordion"
        :class="{
            'is-open': isOpen,
            'c-accordion--reverse': reverse
        }"
    >
        <header
            class="c-accordion__header"
            @click.prevent="toggle"
        >
            <slot name="title">
                <h2 class="c-accordion__title || t-s">{{ title }}</h2>
            </slot>
            <button class="c-accordion__btn" aria-label="Ouvrir/Fermer">
                <Icon icon="arrow-down"/>
            </button>
        </header>
        <div ref="container" class="c-accordion__container">
            <div ref="inner" class="c-accordion__content">
                <slot name="content">
                    <div
                        class="t-cms"
                        v-html="content"
                    ></div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from 'templates/objects/Icon';
import gsap from 'gsap';
import scrollTo from 'gsap/ScrollToPlugin';

export default {
    name: 'Accordion',
    components: {
        Icon,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        content: {
            type: String,
            default: '',
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        reverse: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOpen: false,
        };
    },
    watch: {
        options() {
            this.$nextTick(() => {
                this.checkOptions();
            });
        },
    },
    mounted() {
        this.defaultOptions = {
            scroll: false,
            watchCSS: false,
            maxHeight: 400,
            open: false,
        };

        this.checkOptions();

        window.addEventListener('resize', this.watchCSS);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.watchCSS);
    },
    methods: {
        checkOptions() {
            this.opts = Object.assign({}, this.defaultOptions, this.options);
            this.isOpen = this.opts.open;
            if (this.opts.watchCSS) {
                this.watchCSS();
            } else {
                this.isActive = true;
            }

            if (this.isOpen) {
                this._open();
            } else {
                this._close();
            }
        },
        _open() {
            if (!this.isActive || !this.$refs.container) return;
            this.isOpen = true;

            const height = this.$refs.inner.offsetHeight;

            TweenLite.to(this.$refs.container, 1, {
                height: this.opts.maxHeight && height > this.opts.maxHeight ? this.opts.maxHeight : height,
                opacity: 1,
                onStart: () => {
                    if (height === this.opts.maxHeight) {
                        this.$refs.container.classList.add('has-scroll');
                    }
                },
                onComplete: () => {
                    TweenLite.set(this.$refs.container, { height: 'auto' });
                },
                ease: Expo.easeInOut,
            });

            if (this.opts.scroll) {
                const scrollY = this.$refs.container.getBoundingClientRect().top + window.scrollY - document.querySelector('.js-header').offsetHeight;
                this.animScroll(scrollY);
            }
        },
        _close() {
            if (!this.isActive || !this.$refs.container) return;
            this.isOpen = false;

            TweenLite.to(this.$refs.container, 1, {
                height: 0,
                opacity: 0,
                onComplete: () => {
                    TweenLite.set(this.$refs.container, { height: 0 });
                    this.$refs.container.classList.remove('has-scroll');
                },
                ease: Expo.easeInOut,
            });

            if (this.opts.scroll) {
                const scrollY = this.$el.getBoundingClientRect().top + window.scrollY;

                this.animScroll(scrollY);
            }
        },
        toggle() {
            if (this.isOpen) {
                this._close();
            } else {
                this._open();
            }
        },
        animScroll(y) {
            TweenLite.to(window, 1, {
                scrollTo: {
                    y,
                    autoKill: false,
                },
                ease: Expo.easeInOut,
            });
        },

        watchCSS() {
            if (!this.opts.watchCSS) return;

            const afterContent = getComputedStyle(this.$el, ':after').content;

            if (afterContent.indexOf('accordion') !== -1) {
                this.isActive = true;
            } else {
                this.isActive = false;
                this.$refs.container.removeAttribute('style');
            }
        },
    },

};
</script>

<style lang="scss">

    .c-accordion {
        margin-top: 1em;
        background-color: $color-light;
        border-radius: $radius;
        box-shadow: $shadow;

        // // Uncomment and add to a media query watchCSS is enabled
        // &:after {
        //     content: "accordion";
        //     display: none;
        // }

        &.is-open {

            .c-accordion__content {
                pointer-events: initial;
            }

            .c-accordion__btn {
                transform: rotate(180deg);
                transition: transform .3s $in-out-circ;
            }
        }

        &--reverse {
            display: flex;
            flex-direction: column;

            .c-accordion__header {
                order: 2
            }

            .c-accordion__container {
                order: 1
            }

            .c-accordion__btn {
                transform: rotate(180deg);
            }

            &.is-open {
                .c-accordion__btn {
                    transform: rotate(360deg);
                }
            }
        }

    }


    .c-accordion__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.5em 2em;
        text-transform: uppercase;
        user-select: none;
        cursor: pointer;
    }


    .c-accordion__title {
        padding-right: 1rem;
    }


    .c-accordion__btn {
        transition: transform .2s $in-out-circ;
    }


    .c-accordion__container {
        height: 0;
        opacity: 0;
        overflow: hidden;
        border-top: $border-light;

        &.has-scroll {

            &:after {
                @include pseudo-el($height: 4em);
                position: absolute;
                bottom: 0;
                left: 0;
                @include linear-gradient(to top, $color-light, $color-light 1.5em, rgba(255, 255, 255, 0) 100%);
                border-bottom-left-radius: $radius;
                border-bottom-right-radius: $radius;
                pointer-events: none;
            }

            .c-accordion__content {
                padding-bottom: 3em;
            }
        }
    }


    .c-accordion__content {
        max-height: 400px;
        padding: 1.5em 2em;
        pointer-events: none;
        overflow: auto;
        -webkit-overflow-scrolling: touch;
    }

</style>
